/**
 * BiteLearnPage — BiteLearn 每日微學習方案 (the product landing page).
 *
 * WHAT CHANGED (2026-11-05)
 * -------------------------
 * This page used to be a hand-coded STANDALONE document:
 * `public/products/bite-learn/index.html` — 765 lines with its own
 * `css/styles.css` (424 lines) and `js/app.js`, served by nginx straight
 * out of `dist/`. It had no site chrome of its own beyond a small local
 * header, and it existed OUTSIDE the React Router, so it could not carry
 * the site navbar, could not be reached through the SPA, and needed its
 * own hand-maintained copy of the canonical / hreflang / JSON-LD head.
 *
 * It is now a React route under `PublicLayout`, exactly like
 * `/products/smart-cindy` and `/products/csp`, so it gains the YADA
 * header bar (CMS-driven menu, dropdowns, mobile drawer, language
 * switcher) and the footer.
 *
 * THE LAYOUT IS UNCHANGED
 * -----------------------
 * The ported markup keeps EVERY class name the standalone used
 * (`.hero`, `.wrap`, `.section-title`, `.shot`, …) and the stylesheet
 * (src/styles/bite-learn.css) is a straight port of the standalone's
 * `css/styles.css`. The only structural changes are the ones the
 * standalone could not make for itself:
 *
 *   - the `<main id="main">` became a `<div>`: the site layout already
 *     renders a `<main id="main-content">` around every route, and a
 *     `<main>` inside a `<main>` is invalid HTML;
 *   - the standalone's own skip link (`.skip` → `#main`) is gone — the
 *     site provides `.skip-link` → `#main-content` for every page;
 *   - the standalone's 中/EN button group is gone — the site navbar has
 *     a language switcher, and the page's language follows the URL.
 *
 * ONE LANGUAGE IN THE DOCUMENT
 * ----------------------------
 * The standalone carried BOTH languages in the DOM at once
 * (`<span class="lang-zh">…</span><span class="lang-en">…</span>`) and
 * hid one of them with `body[data-lang]` CSS. That is a duplicate-content
 * problem: every locale URL served the same two-language document.
 *
 * Now the three locale bundles each carry their own copy under
 * `biteLearn.landing.*` and the page renders EXACTLY ONE of them, chosen
 * by the URL locale. The CMS slots work the same way as every other
 * page: the i18n bundle defines the SHAPE (how many cards, how many FAQ
 * entries, in which order) and a CMS override supplies only VALUES,
 * merged over that shape — so an edit can never change the layout.
 *
 * THE FAQ IS ONE SLOT, TWO RENDERINGS
 * -----------------------------------
 * The visible `<details>` list and the FAQPage JSON-LD are both derived
 * from the same merged value, so the answers a visitor reads and the
 * answers the search engine reads can never disagree.
 *
 * THE STYLESHEET IS IMPORTED FROM src/main.tsx
 * --------------------------------------------
 * NOT from here: the prerender pass (scripts/prerender.tsx) renders this
 * component in plain Node via tsx, and Node cannot import a `.css` file
 * — a component-level CSS import fails the whole prerender with
 * `Unknown file extension ".css"`.
 *
 * IMAGES
 * ------
 * Every image is referenced by its PUBLIC path, never imported from
 * src/assets — the prerender pass renders in plain Node and cannot
 * resolve asset imports. The files live in `public/products/bite-learn/
 * assets/`, which is where `src/content/heroLegacy.ts` and the CMS
 * media rows already point.
 */
import { useLocation, Link } from 'react-router-dom'
import { buildCanonicalUrl, SITE_URL } from '@/utils/siteMeta'
import { buildLocalePath, parseLocaleFromPath } from '@/utils/localePath'
import SEO from '@/utils/seo'
import { buildBreadcrumbList, buildFAQPage } from '@/utils/structuredData'
import { useSlotObject, useSlotT } from '@/cms/useSlotT'

/** The hero / OG image — also the CMS row's artwork. */
const IMG_HERO = '/products/bite-learn/assets/solution.jpg'
const IMG_PAIN = '/products/bite-learn/assets/pain.jpg'
const IMG_WORKFLOW = '/products/bite-learn/assets/workflow.jpg'

/**
 * The gallery's screenshots, in the order the slot's `items` array holds
 * them. The FILES are code (an operator must not be able to point a
 * screenshot slot at an arbitrary path); the CAPTION and the ALT TEXT
 * are copy, so they live in the slot.
 */
const GALLERY_IMAGES = [
  { file: 'dashboard.jpg', width: 1400, height: 713 },
  { file: 'curriculum.jpg', width: 1400, height: 763 },
  { file: 'ai-generate.jpg', width: 1083, height: 776 },
  { file: 'question-pool.jpg', width: 1400, height: 859 },
  { file: 'micro-lesson.jpg', width: 1400, height: 861 },
  { file: 'whatsapp-preview.jpg', width: 1202, height: 868 },
  { file: 'mini-exercise.jpg', width: 1190, height: 897 },
  { file: 'statistics.jpg', width: 1241, height: 851 },
  { file: 'worksheets.jpg', width: 1400, height: 516 },
] as const

/**
 * The on-page anchor navigation. The LABELS are copy (the slot), the
 * TARGETS are structure (the code) — the two are paired by index.
 */
const NAV_ANCHORS = [
  '#problem',
  '#solution',
  '#uses',
  '#features',
  '#flow',
  '#product',
  '#faq',
] as const

interface HeroSlot {
  eyebrow?: string
  title?: string
  lede?: string
  imageAlt?: string
}

interface StatItem {
  value?: string
  label?: string
}

interface StatsSlot {
  items?: StatItem[]
}

interface ProblemSlot {
  title?: string
  intro?: string
  items?: string[]
  imageAlt?: string
}

interface SolutionSlot {
  title?: string
  items?: string[]
  imageAlt?: string
}

interface CardItem {
  title?: string
  text?: string
}

interface UsesSlot {
  title?: string
  items?: CardItem[]
}

interface FeaturesSlot {
  title?: string
  items?: CardItem[]
}

interface FlowSlot {
  title?: string
  items?: CardItem[]
  band?: { title?: string; text?: string }
}

interface ShotItem {
  title?: string
  text?: string
  imageAlt?: string
}

interface GallerySlot {
  title?: string
  intro?: string
  items?: ShotItem[]
}

interface FaqItem {
  question?: string
  answer?: string
}

interface FaqSlot {
  title?: string
  items?: FaqItem[]
}

interface NavSlot {
  backHome?: string
  items?: { label?: string }[]
}

interface BreadcrumbSlot {
  home?: string
  products?: string
  self?: string
}

/**
 * asText — the missing-string guard.
 *
 * i18next echoes the KEY back when a translation is missing, so a
 * missing string would otherwise reach the page as a dotted key path
 * ("biteLearn.landing.hero.title"). A value that still looks like one of
 * our namespaces is therefore treated as missing and renders as an empty
 * string. Same pattern as `asText()` in CspPage.
 */
function asText(value: unknown): string {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('biteLearn.landing.') || trimmed.startsWith('seo.')) return ''
  return value
}

/**
 * asList — the missing-list guard.
 *
 * With `returnObjects: true` a missing key comes back as the key STRING,
 * not an array, so anything that is not an array is treated as an empty
 * list rather than a crash in `.map()`. The array inside a json slot is
 * merged onto the i18n default, which keeps the default's length, so the
 * number of cards on the page cannot change.
 */
function asList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export default function BiteLearnPage() {
  const { pathname } = useLocation()

  // The page's language follows the URL. The locale is passed to the slot
  // resolvers EXPLICITLY, so a CMS override is read against the same
  // locale the copy is rendered in.
  const locale = parseLocaleFromPath(pathname)

  const slotObj = useSlotObject('bite-learn', locale)
  const slotT = useSlotT('bite-learn', locale)

  const seoTitle = asText(slotT('seo.biteLearnTitle'))
  const seoDescription = asText(slotT('seo.biteLearnDescription'))
  const seoKeywords = asText(slotT('seo.biteLearnKeywords'))

  const hero = slotObj('biteLearn.landing.hero') as HeroSlot
  const stats = slotObj('biteLearn.landing.stats') as StatsSlot
  const problem = slotObj('biteLearn.landing.problem') as ProblemSlot
  const solution = slotObj('biteLearn.landing.solution') as SolutionSlot
  const uses = slotObj('biteLearn.landing.uses') as UsesSlot
  const features = slotObj('biteLearn.landing.features') as FeaturesSlot
  const flow = slotObj('biteLearn.landing.flow') as FlowSlot
  const gallery = slotObj('biteLearn.landing.gallery') as GallerySlot
  const faq = slotObj('biteLearn.landing.faq') as FaqSlot
  const nav = slotObj('biteLearn.landing.nav') as NavSlot
  const breadcrumb = slotObj('biteLearn.landing.breadcrumb') as BreadcrumbSlot
  const footer = slotObj('biteLearn.landing.footer') as { text?: string }

  const statItems = asList<StatItem>(stats.items)
  const problemItems = asList<string>(problem.items)
  const solutionItems = asList<string>(solution.items)
  const useItems = asList<CardItem>(uses.items)
  const featureItems = asList<CardItem>(features.items)
  const flowItems = asList<CardItem>(flow.items)
  const galleryItems = asList<ShotItem>(gallery.items)
  const faqItems = asList<FaqItem>(faq.items)
  const navItems = asList<{ label?: string }>(nav.items)

  const faqSchema = faqItems
    .filter((item) => item.question && item.answer)
    .map((item) => ({ question: asText(item.question), answer: asText(item.answer) }))

  /**
   * The SoftwareApplication node. The standalone carried it as a
   * hand-written JSON-LD block; it is rebuilt here from the same copy so
   * the two cannot drift apart.
   */
  const canonicalUrl = buildCanonicalUrl(pathname)
  const softwareApplication = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BiteLearn Solution',
    alternateName: 'BiteLearn',
    url: canonicalUrl,
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
    description: seoDescription,
    image: `${SITE_URL}${IMG_HERO}`,
    publisher: {
      '@type': 'Organization',
      name: '青年耀進發展協會 YADA',
      url: `${SITE_URL}/`,
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'HKD',
      availability: 'https://schema.org/InStock',
      url: canonicalUrl,
    },
  }

  return (
    <div className="bl-root bl-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        ogImage={IMG_HERO}
        jsonLd={[
          buildBreadcrumbList([
            { name: asText(breadcrumb.home), path: buildLocalePath('/', locale) },
            { name: asText(breadcrumb.products), path: buildLocalePath('/products', locale) },
            {
              name: asText(breadcrumb.self),
              path: buildLocalePath('/products/bite-learn', locale),
            },
          ]),
          buildFAQPage(faqSchema),
          softwareApplication,
        ]}
      />

      {/* ── Page header ────────────────────────────────────────────────── */}
      {/* The standalone's own chrome, kept as page content: it carries the
          on-page anchor navigation, which the site navbar cannot replace
          (those links point at THIS page's sections). The 中/EN switch it
          used to carry is gone — the site navbar has a language switcher
          and the page's language follows the URL. */}
      <header className="site-header">
        <div className="wrap header-inner">
          <Link className="back-home" to={buildLocalePath('/', locale)}>
            <span className="back-home-arrow" aria-hidden="true">
              ←
            </span>
            {asText(nav.backHome)}
          </Link>
          <a className="brand" href="#top">
            <span className="mark">BL</span>
            BiteLearn
          </a>
          <nav className="nav" aria-label="Page">
            {navItems.map((item, index) => {
              const href = NAV_ANCHORS[index]
              if (!href) return null
              return (
                <a key={href} href={href}>
                  {asText(item.label)}
                </a>
              )
            })}
          </nav>
        </div>
      </header>

      <div>
        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="hero" id="top">
          <div className="wrap hero-grid">
            <div>
              <span className="eyebrow">{asText(hero.eyebrow)}</span>
              <h1>{asText(hero.title)}</h1>
              <p className="lede">{asText(hero.lede)}</p>
            </div>
            <div className="hero-card">
              <img
                src={IMG_HERO}
                alt={asText(hero.imageAlt)}
                width={1400}
                height={711}
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────────────────── */}
        <div className="wrap">
          <div className="stats">
            {statItems.map((item, index) => (
              <div className="stat" key={`${index}-${asText(item.value)}`}>
                <strong>{asText(item.value)}</strong>
                <span>{asText(item.label)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Problem ───────────────────────────────────────────────────── */}
        <section id="problem">
          <div className="wrap split">
            <div>
              <h2 className="section-title">{asText(problem.title)}</h2>
              <p className="section-intro">{asText(problem.intro)}</p>
              <ul className="list">
                {problemItems.map((item, index) => (
                  <li key={`${index}-${asText(item).slice(0, 24)}`}>{asText(item)}</li>
                ))}
              </ul>
            </div>
            <figure className="panel">
              <img
                src={IMG_PAIN}
                alt={asText(problem.imageAlt)}
                width={1400}
                height={787}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
        </section>

        {/* ── Solution ──────────────────────────────────────────────────── */}
        <section id="solution">
          <div className="wrap split">
            <figure className="panel">
              <img
                src={IMG_WORKFLOW}
                alt={asText(solution.imageAlt)}
                width={1400}
                height={770}
                loading="lazy"
                decoding="async"
              />
            </figure>
            <div>
              <h2 className="section-title">{asText(solution.title)}</h2>
              <ul className="list">
                {solutionItems.map((item, index) => (
                  <li key={`${index}-${asText(item).slice(0, 24)}`}>{asText(item)}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── Uses ──────────────────────────────────────────────────────── */}
        <section id="uses">
          <div className="wrap">
            <h2 className="section-title">{asText(uses.title)}</h2>
            <div className="uses">
              {useItems.map((item, index) => (
                <article className="use" key={`${index}-${asText(item.title)}`}>
                  <h3>{asText(item.title)}</h3>
                  <p>{asText(item.text)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Features ──────────────────────────────────────────────────── */}
        <section id="features">
          <div className="wrap">
            <h2 className="section-title">{asText(features.title)}</h2>
            <div className="features">
              {featureItems.map((item, index) => (
                <article className="feature" key={`${index}-${asText(item.title)}`}>
                  <h3>{asText(item.title)}</h3>
                  <p>{asText(item.text)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Flow ──────────────────────────────────────────────────────── */}
        <section id="flow">
          <div className="wrap split">
            <div>
              <h2 className="section-title">{asText(flow.title)}</h2>
              <ol className="flow">
                {flowItems.map((item, index) => (
                  <li key={`${index}-${asText(item.title)}`}>
                    <span>
                      <strong>{asText(item.title)}</strong>
                      <br />
                      {asText(item.text)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="band">
              <h2>{asText(flow.band?.title)}</h2>
              <p>{asText(flow.band?.text)}</p>
            </div>
          </div>
        </section>

        {/* ── Product screens ───────────────────────────────────────────── */}
        <section id="product">
          <div className="wrap">
            <h2 className="section-title">{asText(gallery.title)}</h2>
            <p className="section-intro">{asText(gallery.intro)}</p>
            <div className="gallery">
              {galleryItems.map((item, index) => {
                const image = GALLERY_IMAGES[index]
                if (!image) return null
                const src = `/products/bite-learn/assets/${image.file}`
                return (
                  <a className="shot" key={`${index}-${image.file}`} href={src}>
                    <img
                      src={src}
                      alt={asText(item.imageAlt)}
                      width={image.width}
                      height={image.height}
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption>
                      <strong>{asText(item.title)}</strong>
                      <span>{asText(item.text)}</span>
                    </figcaption>
                  </a>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────── */}
        <section id="faq">
          <div className="wrap">
            <h2 className="section-title">{asText(faq.title)}</h2>
            <div className="faq">
              {faqItems.map((item, index) => (
                <details key={`${index}-${asText(item.question).slice(0, 24)}`} open={index === 0}>
                  <summary>{asText(item.question)}</summary>
                  <p>{asText(item.answer)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ── The standalone's own one-line footer ────────────────────────── */}
      <footer>
        <div className="wrap">{asText(footer.text)}</div>
      </footer>
    </div>
  )
}
